import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './layout/AppLayout';
import { Overview } from './pages/Overview';
import { MapPage } from './pages/MapPage';
import { Reminders } from './pages/Reminders';
import { CustomerDetail } from './pages/CustomerDetail';
import { ExtinguisherEdit } from './pages/ExtinguisherEdit';
import { HoseEdit } from './pages/HoseEdit';
import { HoseReport } from './pages/HoseReport';
import { ExtinguisherReport } from './pages/ExtinguisherReport';
import { DeviceHistory } from './pages/DeviceHistory';
import { Invoice } from './pages/Invoice';
import { HoseOptions } from './pages/admin/HoseOptions';
import { Manufacturers } from './pages/admin/Manufacturers';
import { DeviceTypes } from './pages/admin/DeviceTypes';
import { SpareParts } from './pages/admin/SpareParts';
import { ServicePrices } from './pages/admin/ServicePrices';
import { Workers } from './pages/admin/Workers';
import { Permissions } from './pages/admin/Permissions';
import { SettingsPage } from './pages/admin/Settings';
import { CalendarPage } from './pages/Calendar';
import { Reports } from './pages/Reports';
import { Login } from './pages/Login';
import { Toasts } from './components/Toasts';
import { ConfirmDialog } from './components/ConfirmDialog';
import { Tour } from './components/Tour';
import { AuthGate } from './components/AuthGate';
import { RequireRole } from './components/RequireRole';
import { RequireSection, HomeRedirect } from './components/RequireSection';
import { SectionId } from './lib/section-permissions';
import { Tenants as SuperTenants } from './pages/superadmin/Tenants';
import { TenantNew } from './pages/superadmin/TenantNew';
import { TenantDetail } from './pages/superadmin/TenantDetail';

// Section-gated route element (client #95): without Lesen on every listed
// section the user is redirected to the first page they may read.
const gate = (
  sections: SectionId | SectionId[],
  page: React.ReactNode
): React.ReactNode => (
  <RequireSection sections={sections}>{page}</RequireSection>
);

export function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          element={
            <AuthGate>
              <AppLayout />
            </AuthGate>
          }
        >
          <Route index element={<HomeRedirect />} />
          {/* /overview is never gated: without customers.read it shows a
              "no access" notice, so it is always a safe landing page. */}
          <Route path="/overview" element={<Overview />} />
          <Route path="/map" element={gate(SectionId.MAP, <MapPage />)} />
          <Route
            path="/reminders"
            element={gate(SectionId.REMINDERS, <Reminders />)}
          />
          <Route
            path="/calendar"
            element={gate(SectionId.CALENDAR, <CalendarPage />)}
          />
          <Route
            path="/reports"
            element={gate(SectionId.REPORTS, <Reports />)}
          />
          <Route
            path="/customer/:id"
            element={gate(SectionId.CUSTOMERS, <CustomerDetail />)}
          />
          <Route
            path="/customer/:id/:tab"
            element={gate(SectionId.CUSTOMERS, <CustomerDetail />)}
          />
          <Route
            path="/customer/:id/extinguisher/:extId"
            element={gate(
              [SectionId.CUSTOMERS, SectionId.EXTINGUISHERS],
              <ExtinguisherEdit />
            )}
          />
          <Route
            path="/customer/:id/hose/:hoseId"
            element={gate([SectionId.CUSTOMERS, SectionId.HOSES], <HoseEdit />)}
          />
          <Route
            path="/customer/:id/hose-report"
            element={gate(
              [SectionId.CUSTOMERS, SectionId.HOSES],
              <HoseReport />
            )}
          />
          <Route
            path="/customer/:id/extinguisher-report"
            element={gate(
              [SectionId.CUSTOMERS, SectionId.EXTINGUISHERS],
              <ExtinguisherReport />
            )}
          />
          <Route
            path="/customer/:id/device/:deviceId/history"
            element={gate(
              [SectionId.CUSTOMERS, SectionId.MAINTENANCE],
              <DeviceHistory />
            )}
          />
          <Route
            path="/customer/:id/invoice"
            element={gate(
              [SectionId.CUSTOMERS, SectionId.INVOICES],
              <Invoice />
            )}
          />
          <Route
            path="/admin/manufacturers"
            element={gate(SectionId.MANUFACTURERS, <Manufacturers />)}
          />
          <Route
            path="/admin/device-types"
            element={gate(SectionId.DEVICE_TYPES, <DeviceTypes />)}
          />
          <Route
            path="/admin/spare-parts"
            element={gate(SectionId.SPARE_PARTS, <SpareParts />)}
          />
          <Route
            path="/admin/service-prices"
            element={gate(SectionId.SERVICE_PRICES, <ServicePrices />)}
          />
          <Route
            path="/admin/workers"
            element={gate(SectionId.WORKERS, <Workers />)}
          />
          <Route
            path="/admin/hose-options"
            element={gate(SectionId.HOSE_OPTIONS, <HoseOptions />)}
          />
          <Route
            path="/admin/settings"
            element={gate(SectionId.SETTINGS, <SettingsPage />)}
          />
          {/* The matrix itself stays admin-only: it is how the rest is set. */}
          <Route
            path="/admin/permissions"
            element={
              <RequireRole roles={['org_admin']}>
                <Permissions />
              </RequireRole>
            }
          />
          <Route
            path="/superadmin"
            element={<Navigate to="/superadmin/tenants" replace />}
          />
          <Route
            path="/superadmin/tenants"
            element={
              <RequireRole roles={['super_admin']}>
                <SuperTenants />
              </RequireRole>
            }
          />
          <Route
            path="/superadmin/tenants/new"
            element={
              <RequireRole roles={['super_admin']}>
                <TenantNew />
              </RequireRole>
            }
          />
          <Route
            path="/superadmin/tenants/:id"
            element={
              <RequireRole roles={['super_admin']}>
                <TenantDetail />
              </RequireRole>
            }
          />
          <Route path="*" element={<HomeRedirect />} />
        </Route>
      </Routes>
      <Toasts />
      <ConfirmDialog />
      <Tour />
    </HashRouter>
  );
}
